// Talles y colores — capa compartida de variantes.
//
// Un producto con stock_tracking = 'variant' guarda su stock por
// (variante, sucursal) en stock_variantes; stock_levels queda como total
// mantenido por la base. Todo movimiento de stock pasa por la RPC
// fn_mover_stock, que decide el universo, controla negativo y registra kardex.
//
// Ver desarrollo/plan-talles-colores-indumentaria.md.

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const normalizeKey = (value) => String(value ?? '').trim().toLowerCase();

export const isVariantTracked = (product) => product?.stock_tracking === 'variant';

export const isVariantActive = (variant) => {
  if (!variant || typeof variant !== 'object') return false;
  if (variant.activo === false) return false;
  if (variant.archivado_at) return false;
  if (variant.attributes?.variant_active === false) return false;
  return true;
};

export const getVariantColor = (variant) => {
  const color = variant?.colores || variant?.color || null;
  if (color && typeof color === 'object') return color;
  if (variant?.color_nombre) return { id: variant.color_id ?? null, nombre: variant.color_nombre, hex: variant.color_hex ?? null };
  return null;
};

export const getVariantAttributesLabel = (variant) => {
  if (!variant || typeof variant !== 'object') return '';
  const colorName = String(getVariantColor(variant)?.nombre || '').trim();
  const talle = String(variant.talle || '').trim();
  return [colorName, talle].filter(Boolean).join(' / ');
};

export const hasVariantAttributes = (variant) =>
  Boolean(variant && (String(variant.talle || '').trim() || variant.color_id));

export const getVariantUnitPrice = (product, variant) => {
  const override = variant?.precio_override;
  if (override !== null && override !== undefined && override !== '' && Number.isFinite(Number(override))) {
    return Number(override);
  }
  return toNumber(product?.sell_price, 0);
};

export const getVariantStockForSite = (stockVariantes, variantId, siteId = null) => {
  const rows = Array.isArray(stockVariantes) ? stockVariantes : [];
  return rows.reduce((acc, row) => {
    if (String(row?.variant_id) !== String(variantId)) return acc;
    if (siteId && String(row?.site_id) !== String(siteId)) return acc;
    return acc + toNumber(row?.quantity, 0);
  }, 0);
};

const compareTalle = (a, b) => {
  const oa = a?.talle_orden;
  const ob = b?.talle_orden;
  const hasA = oa !== null && oa !== undefined;
  const hasB = ob !== null && ob !== undefined;
  if (hasA && hasB && oa !== ob) return oa - ob;
  if (hasA !== hasB) return hasA ? -1 : 1;
  return String(a?.talle || '').localeCompare(String(b?.talle || ''), 'es', { numeric: true });
};

const compareColor = (a, b) =>
  String(getVariantColor(a)?.nombre || '').localeCompare(String(getVariantColor(b)?.nombre || ''), 'es');

export const sortVariantsByCurve = (variants) =>
  [...(Array.isArray(variants) ? variants : [])].sort((a, b) => compareColor(a, b) || compareTalle(a, b));

const cellKey = (colorId, talle) => `${colorId ?? ''}|${normalizeKey(talle)}`;

export const buildVariantGrid = (variants, { stockVariantes = [], siteId = null } = {}) => {
  const active = sortVariantsByCurve((Array.isArray(variants) ? variants : []).filter(isVariantActive));

  const colorMap = new Map();
  const talleMap = new Map();
  const cells = new Map();

  for (const variant of active) {
    const color = getVariantColor(variant);
    const colorId = variant.color_id ?? color?.id ?? null;
    if (!colorMap.has(colorId ?? '')) {
      colorMap.set(colorId ?? '', { id: colorId, nombre: color?.nombre || '', hex: color?.hex || null });
    }

    const talle = String(variant.talle || '').trim();
    const tKey = normalizeKey(talle);
    const orden = variant.talle_orden ?? null;
    const current = talleMap.get(tKey);
    if (!current || (orden !== null && (current.orden === null || orden < current.orden))) {
      talleMap.set(tKey, { talle: current?.talle || talle, orden });
    }

    cells.set(cellKey(colorId, talle), {
      variant,
      stock: getVariantStockForSite(stockVariantes, variant.id, siteId),
    });
  }

  const talles = [...talleMap.values()]
    .sort((a, b) => compareTalle({ talle: a.talle, talle_orden: a.orden }, { talle: b.talle, talle_orden: b.orden }))
    .map((t) => t.talle);

  const colores = [...colorMap.values()].sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es'));

  return {
    talles,
    colores,
    getCell: (colorId, talle) => cells.get(cellKey(colorId, talle)) || null,
  };
};

export const parseTalleList = (text) => {
  const seen = new Set();
  const result = [];
  for (const raw of String(text ?? '').split(/[,;\s]+/)) {
    const talle = raw.trim();
    if (!talle) continue;
    const key = talle.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(talle);
  }
  return result;
};

const skuPart = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const buildVariantSku = ({ codigo, colorCodigo, talle }) =>
  [skuPart(codigo), skuPart(colorCodigo), skuPart(talle)].filter(Boolean).join('-');

export const buildVariantCombinations = ({ talles = [], colores = [] } = {}) => {
  const t = Array.isArray(talles) ? talles : [];
  const c = Array.isArray(colores) ? colores : [];
  if (!t.length && !c.length) return [];
  if (!c.length) return t.map((talle, idx) => ({ talle, talle_orden: idx, color: null }));
  if (!t.length) return c.map((color) => ({ talle: null, talle_orden: null, color }));
  const result = [];
  t.forEach((talle, idx) => {
    c.forEach((color) => result.push({ talle, talle_orden: idx, color }));
  });
  return result;
};

export const findVariantByCode = (products, code) => {
  const needle = normalizeKey(code);
  if (!needle) return null;
  for (const product of Array.isArray(products) ? products : []) {
    for (const variant of Array.isArray(product?.product_variants) ? product.product_variants : []) {
      if (!hasVariantAttributes(variant) || !isVariantActive(variant)) continue;
      if (normalizeKey(variant.barcode) === needle || normalizeKey(variant.sku) === needle) {
        return { product, variant };
      }
    }
  }
  return null;
};

export const groupStockOperations = (operations) => {
  const map = new Map();
  for (const op of Array.isArray(operations) ? operations : []) {
    const variantId = op?.variantId ?? null;
    const key = `${op?.productId}|${variantId}`;
    const current = map.get(key);
    if (current) {
      current.quantity += toNumber(op?.quantity, 0);
    } else {
      map.set(key, { ...op, variantId, quantity: toNumber(op?.quantity, 0) });
    }
  }
  return [...map.values()];
};

export const applyStockDelta = async ({
  supabase,
  organizationId,
  productId,
  variantId = null,
  siteId,
  delta,
  tipo = null,
  motivo = null,
  canal = null,
  allowNegative = null,
}) => {
  if (!supabase) throw new Error('Falta el cliente de base de datos.');
  if (!organizationId) throw new Error('Falta la organización para mover stock.');
  if (!productId) throw new Error('Falta el producto para mover stock.');
  if (!siteId) throw new Error('Falta la sucursal para mover stock.');
  const amount = Number(delta);
  if (!Number.isFinite(amount)) throw new Error('La cantidad a mover no es válida.');

  const { data, error } = await supabase.rpc('fn_mover_stock', {
    p_org: organizationId,
    p_product: productId,
    p_variant: variantId,
    p_site: siteId,
    p_delta: amount,
    p_tipo: tipo,
    p_motivo: motivo,
    p_canal: canal,
    p_allow_negative: allowNegative,
  });

  if (error) {
    throw new Error(error.message || 'No se pudo mover el stock.');
  }
  return data;
};

// --- Reposición por talle (anulaciones y devoluciones) ---------------------------

/**
 * Separa ítems de productos con talles (agrupados por producto + variante) del
 * resto, que sigue su camino de siempre sin tocar.
 */
export const splitVariantItems = ({ items, variantTrackedIds }) => {
  const tracked = variantTrackedIds instanceof Set ? variantTrackedIds : new Set(variantTrackedIds || []);
  const variantMap = new Map();
  const legacyItems = [];
  for (const item of Array.isArray(items) ? items : []) {
    if (!tracked.has(item?.product_id)) {
      legacyItems.push(item);
      continue;
    }
    const key = `${item.product_id}|${item.variant_id ?? ''}`;
    const current = variantMap.get(key);
    if (current) current.quantity += toNumber(item.quantity, 0);
    else variantMap.set(key, { product_id: item.product_id, variant_id: item.variant_id ?? null, quantity: toNumber(item.quantity, 0) });
  }
  return { variantItems: [...variantMap.values()], legacyItems };
};

const SIN_CLASIFICAR = 'Sin clasificar';

const resolveSinClasificar = async (supabase, productId) => {
  const buscar = () => supabase
    .from('product_variants')
    .select('id')
    .eq('product_id', productId)
    .eq('talle', SIN_CLASIFICAR)
    .is('color_id', null)
    .is('archivado_at', null)
    .limit(1)
    .maybeSingle();

  let { data } = await buscar();
  if (data?.id) return data.id;

  const { error } = await supabase.rpc('fn_guardar_variantes_producto', {
    p_product: productId,
    p_variantes: [{ talle: SIN_CLASIFICAR, talle_orden: 9999 }],
    p_distribucion: [],
  });
  if (error) throw new Error(error.message || 'No se pudo crear la variante "Sin clasificar".');
  ({ data } = await buscar());
  if (!data?.id) throw new Error('No se encontró la variante "Sin clasificar".');
  return data.id;
};

/**
 * Repone stock por talle. Si la línea no tiene variante (venta anterior a
 * activar talles en ese producto), va a "Sin clasificar" (se crea si falta).
 */
export const restoreVariantStock = async ({ supabase, organizationId, siteId, items, motivo, canal = null }) => {
  if (!siteId) throw new Error('Falta la sucursal para reponer el stock.');
  let restored = 0;
  let sinClasificar = 0;
  const lista = (Array.isArray(items) ? items : []).filter((item) => toNumber(item?.quantity, 0) > 0);

  // Ventas anteriores a convertir el producto apuntan a la variante "Base",
  // que quedó archivada: reponer ahí dejaría stock invisible.
  const ids = [...new Set(lista.map((item) => item.variant_id).filter(Boolean))];
  const archivadas = new Set();
  if (ids.length) {
    const { data, error } = await supabase.from('product_variants').select('id, archivado_at').in('id', ids);
    if (error) throw new Error(error.message || 'No se pudieron leer las variantes.');
    for (const row of data || []) if (row?.archivado_at) archivadas.add(row.id);
  }

  for (const item of lista) {
    const qty = toNumber(item?.quantity, 0);
    let variantId = item.variant_id && !archivadas.has(item.variant_id) ? item.variant_id : null;
    if (!variantId) {
      variantId = await resolveSinClasificar(supabase, item.product_id);
      sinClasificar += 1;
    }
    await applyStockDelta({
      supabase,
      organizationId,
      productId: item.product_id,
      variantId,
      siteId,
      delta: qty,
      tipo: 'Ingreso',
      motivo,
      canal,
      allowNegative: true,
    });
    restored += 1;
  }
  return { restored, sinClasificar };
};

// --- Inventario por talle (ajuste y transferencia) ----------------------------

/**
 * Opciones de variante para mover stock a mano: activas, en orden de curva
 * ("Sin clasificar" siempre al final) y con el stock de la sucursal indicada.
 * Producto clásico → [] (se ajusta a nivel producto como siempre).
 */
export const listStockVariantOptions = (product, siteId = null) => {
  if (!isVariantTracked(product)) return [];
  const activas = (Array.isArray(product?.product_variants) ? product.product_variants : [])
    .filter((variant) => isVariantActive(variant) && hasVariantAttributes(variant));
  const ordenadas = sortVariantsByCurve(activas);
  const esSinClasificar = (variant) => String(variant?.talle || '').trim().toLowerCase() === SIN_CLASIFICAR.toLowerCase();
  return [...ordenadas.filter((v) => !esSinClasificar(v)), ...ordenadas.filter(esSinClasificar)].map((variant) => ({
    id: variant.id,
    label: getVariantAttributesLabel(variant) || String(variant.talle || '').trim(),
    stock: getVariantStockForSite(product?.stock_variantes, variant.id, siteId),
    variant,
  }));
};

// --- Ventas por talle (grilla del producto) ------------------------------------

const VENTA_EXCLUIDA_STATUS = new Set(['canceled', 'cancelled', 'anulada', 'quote']);

/**
 * Filas de sale_items (con `sales` embebida) → { [variantId]: unidades }.
 * Mismo criterio de exclusión que Rentabilidad: anuladas/canceladas,
 * presupuestos y ventas inactivas no cuentan. Opcional: una sola sucursal.
 */
export const buildVariantSalesMap = (rows, { siteId = null } = {}) => (Array.isArray(rows) ? rows : []).reduce((acc, row) => {
  const venta = row?.sales;
  if (!row?.variant_id || !venta) return acc;
  if (venta.active === false) return acc;
  if (VENTA_EXCLUIDA_STATUS.has(String(venta.status || '').toLowerCase())) return acc;
  if (String(venta.invoice_status || '').toLowerCase() === 'presupuesto') return acc;
  if (siteId && String(venta.site_id || '') !== String(siteId)) return acc;
  acc[row.variant_id] = (acc[row.variant_id] || 0) + toNumber(row.quantity, 0);
  return acc;
}, {});

// --- Cambios de talle: a qué talle reponer al devolver/anular ------------------

/**
 * Una línea que tuvo cambios de talle (fn_cambio_talle) ya no está toda en el
 * talle vendido: el cliente se llevó los talles de destino. Al devolver o
 * anular, lo que vuelve se imputa primero a esos talles (en orden de fecha,
 * hasta la cantidad de cada cambio) y el resto al talle vendido.
 * Líneas sin sale_item_id o sin cambios quedan como están.
 */
export const redistribuirPorCambiosTalle = (lineas, cambios, { devueltoPorItem = {} } = {}) => {
  const porItem = new Map();
  for (const cambio of Array.isArray(cambios) ? cambios : []) {
    if (!cambio?.sale_item_id || !cambio?.variant_destino) continue;
    const lista = porItem.get(cambio.sale_item_id) || [];
    lista.push(cambio);
    porItem.set(cambio.sale_item_id, lista);
  }
  const salida = [];
  for (const lineaOriginal of Array.isArray(lineas) ? lineas : []) {
    const lista = lineaOriginal?.sale_item_id ? porItem.get(lineaOriginal.sale_item_id) : null;
    if (!lista || lista.length === 0) {
      salida.push(lineaOriginal);
      continue;
    }
    let restante = toNumber(lineaOriginal.quantity, 0);
    // Devoluciones anteriores de esta línea ya consumieron (en el mismo orden)
    // parte de los talles de los cambios.
    let yaDevuelto = toNumber(devueltoPorItem?.[lineaOriginal.sale_item_id], 0);
    const ordenados = [...lista].sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')));
    for (const cambio of ordenados) {
      if (restante <= 0) break;
      let disponibleCambio = toNumber(cambio.cantidad, 0);
      const consumido = Math.min(yaDevuelto, disponibleCambio);
      disponibleCambio -= consumido;
      yaDevuelto -= consumido;
      const cantidad = Math.min(restante, disponibleCambio);
      if (cantidad <= 0) continue;
      salida.push({ ...lineaOriginal, variant_id: cambio.variant_destino, quantity: cantidad });
      restante -= cantidad;
    }
    if (restante > 0) salida.push({ ...lineaOriginal, quantity: restante });
  }
  return salida;
};

/** Lee los cambios de talle de un conjunto de líneas de venta. */
export const fetchCambiosTalle = async (supabase, saleItemIds) => {
  const ids = [...new Set((saleItemIds || []).filter(Boolean))];
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from('cambios_talle')
    .select('sale_item_id, variant_destino, cantidad, created_at')
    .in('sale_item_id', ids);
  if (error) throw new Error(error.message || 'No se pudieron leer los cambios de talle.');
  return data || [];
};
